import { useHeartbeat } from '@/api/locations/useHeartbeat';
import logo from '@/assets/gametruck-fireball.svg';
import { Avatar } from '@/components/Elements';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/elements/dropdown-menu';
import { MainMenuItems } from '@/config/mainmenu';
import { PolicyGate, useAuthorization } from '@/features/auth/authorization';
import { AuthContext } from '@/providers/auth';
import { LocationContext } from '@/providers/location';
import { useTheme } from '@/providers/theme';
import { cn } from '@/utils/format';
import {
  ArrowLeftStartOnRectangleIcon,
  ComputerDesktopIcon,
  MoonIcon,
  SunIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { Link, useMatchRoute, useNavigate } from '@tanstack/react-router';
import { useContext, useMemo } from 'react';
import { isAndroid, isTablet } from 'react-device-detect';
import { MobileNav } from './Mobile/MobileNav';

interface MainNavigationProps {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
}

export const MainNavigation = ({ mobileMenuOpen, setMobileMenuOpen }: MainNavigationProps) => {
  const { waiting, eventsWaiting, unreadMessages } = useHeartbeat();
  const { user, logout } = useContext(AuthContext);
  const { activeLocation } = useContext(LocationContext);
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { checkPolicy } = useAuthorization();

  const menuItems = useMemo(
    () =>
      MainMenuItems.filter((item) => {
        return item.access_rights.every((right: string) => {
          return checkPolicy({ policy: right, meta: { location: activeLocation } });
        });
      }),
    [activeLocation],
  );

  const activatedMenuItems = menuItems.map((item) => {
    if (item.path === '/') {
      return item;
    }

    const sectionPath = item.path.split('/')[1] ?? '';
    item.active = matchRoute({ to: sectionPath, fuzzy: true }) !== false;

    return item;
  });

  const userInitials = user ? `${user.first_name[0] ?? '?'}${user.last_name[0] ?? '?'}` : '??';

  const handleProfileClick = () => {
    navigate({ to: '/account/user-profile' });
  };

  const handleThemeChange = (nextTheme: string) => {
    if (nextTheme === 'light' || nextTheme === 'dark' || nextTheme === 'system') {
      setTheme(nextTheme);
    }
  };

  return (
    <>
      {/* Narrow sidebar */}
      <div
        className={cn(
          'h-full',
          isAndroid && isTablet ? 'h-[calc(100vh-95px)]' : '',
          isAndroid && !isTablet ? 'h-[calc(100vh-55px)]' : '',
          'hidden md:block md:w-10 lg:w-20 xl:w-24 overflow-y-auto bg-gt-green-500 dark:bg-background/95 border-r',
        )}
      >
        <div className="flex h-full w-full flex-col items-center py-2 lg:py-4">
          <div className="flex shrink-0 items-center">
            <img className="mx-auto px-2 my-2 w-16" src={logo} alt="GameTruck" />
          </div>
          <div className="lg:mt-6 w-full flex-1 space-y-1 px-2">
            {activatedMenuItems.map((item) => (
              <Link
                key={item.label}
                to={item.path}
                search={item.search}
                params={{}}
                className={cn(
                  'group flex w-full flex-col items-center rounded-md px-2 py-1 xl:py-2 text-xs tracking-tight font-semibold transition-colors',
                  '[&.active]:bg-gt-green-600/70 [&.active]:hover:bg-gt-green-600',
                  'text-white dark:text-foreground/70 hover:bg-gt-green-700/30',
                  item.active ? 'bg-gt-green-600/70 hover:bg-gt-green-600' : '',
                )}
              >
                <span className="flex flex-col lg:flex-row justify-center items-center space-y-0.5 lg:space-y-0 lg:space-x-0.5">
                  <item.icon
                    className={cn(
                      'text-white dark:text-foreground/70 group-hover:text-white',
                      'size-5 lg:size-4',
                    )}
                    aria-hidden="true"
                  />
                  <PolicyGate policy="messages.view">
                    {item.label === 'Messages' && !!unreadMessages && (
                      <div className="bg-destructive text-destructive-foreground dark:text-black/80 rounded-3xl w-fit px-1 max-w-8 mx-auto text-center text-[10px] font-bold">
                        {unreadMessages}
                      </div>
                    )}
                    {item.label === 'Leads' && !!waiting && (
                      <div className="bg-destructive text-destructive-foreground dark:text-black/80 rounded-3xl w-fit px-1 max-w-8 mx-auto text-center text-[10px] font-bold">
                        {waiting}
                      </div>
                    )}
                    {item.label === 'Events' && !!eventsWaiting && (
                      <div className="bg-destructive text-destructive-foreground dark:text-black/80 rounded-3xl w-fit px-1 max-w-8 mx-auto text-center text-[10px] font-bold">
                        {eventsWaiting}
                      </div>
                    )}
                  </PolicyGate>
                </span>
                <span className="block mt-1">
                  <span className="hidden lg:block">{item.label}</span>
                </span>
              </Link>
            ))}
          </div>

          {user && (
            <div className="mt-auto w-full px-2 pt-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="Open user menu"
                    className="group flex w-full items-center justify-center rounded-md px-2 py-1 xl:py-2 text-white dark:text-foreground/70 hover:bg-gt-green-700/30"
                  >
                    <Avatar
                      alt={user.full_name}
                      src={user.avatar}
                      fallback={userInitials}
                      size="sm"
                      className="h-8 w-8 lg:h-10 lg:w-10 xl:h-12 xl:w-12"
                    />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="right" align="end" className="w-44">
                  <DropdownMenuItem className="cursor-pointer" onClick={handleProfileClick}>
                    <UserCircleIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Theme</DropdownMenuLabel>
                  <DropdownMenuRadioGroup value={theme} onValueChange={handleThemeChange}>
                    <DropdownMenuRadioItem value="light" className="cursor-pointer">
                      <SunIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                      Light
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="dark" className="cursor-pointer">
                      <MoonIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                      Dark
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="system" className="cursor-pointer">
                      <ComputerDesktopIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                      System
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="cursor-pointer text-destructive focus:text-destructive"
                    onClick={logout}
                  >
                    <ArrowLeftStartOnRectangleIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      <MobileNav open={mobileMenuOpen} setOpen={setMobileMenuOpen} />
    </>
  );
};
